import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, Input, Button, Tag, Typography, Space, message as antdMessage, Popconfirm, Tooltip, Avatar } from 'antd';
import { 
  SendOutlined, 
  RobotOutlined, 
  UserOutlined, 
  DeleteOutlined, 
  DatabaseOutlined, 
  ThunderboltOutlined,
  CheckOutlined,
  MoreOutlined,
  ArrowLeftOutlined,
  PhoneOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import apiClient from '../api';

const { TextArea } = Input;

// WhatsApp Colors
const WA_TEAL = '#075e54';
const WA_GREEN = '#128c7e';
const WA_LIGHT_GREEN = '#dcf8c6';
const WA_BG = '#e5ddd5'; // Classic WhatsApp wallpaper color
const WA_INPUT_BG = '#f0f0f0';

const LiveTest = () => {
  const [sessionId, setSessionId] = useState(() => {
    try {
      const saved = localStorage.getItem('live_test_session_id');
      if (saved) return saved;
      const fresh = `session_${Date.now()}`;
      localStorage.setItem('live_test_session_id', fresh);
      return fresh;
    } catch {
      return `session_${Date.now()}`;
    }
  });
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [lastAgent, setLastAgent] = useState(null);
  const [account, setAccount] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadAccount = useCallback(async () => {
    try {
      const res = await apiClient.get('/accounts/me');
      if (res.data.success) {
        setAccount(res.data.account);
      }
    } catch (_) {}
  }, []);

  useEffect(() => { loadAccount(); }, [loadAccount]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await apiClient.get(`/test-chat/${sessionId}/history`);
      if (res.data.success && res.data.messages?.length) {
        setMessages(res.data.messages);
        const lastWithAgent = [...res.data.messages].reverse().find(m => m.agentUsed);
        if (lastWithAgent) setLastAgent(lastWithAgent.agentUsed);
      }
    } catch (_) {}
    finally { setLoadingHistory(false); }
  }, [sessionId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleClear = async () => {
    try {
      await apiClient.delete(`/test-chat/${sessionId}/history`);
      localStorage.removeItem('live_test_session_id');
      const fresh = `session_${Date.now()}`;
      localStorage.setItem('live_test_session_id', fresh);
      setSessionId(fresh);
      setMessages([]);
      setLastAgent(null);
      antdMessage.success('Chat history cleared');
    } catch (err) {
      antdMessage.error('Failed to clear: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSend = async () => {
    const text = (input || '').trim();
    if (!text || sending) return;

    setMessages(prev => [...prev, { role: 'user', content: text, timestamp: new Date() }]);
    setInput('');
    setSending(true);

    try {
      const res = await apiClient.post('/test-chat', {
        message: text,
        sessionId,
      });
      if (res.data.success) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: res.data.reply || '', 
          agentUsed: res.data.agentUsed,
          timestamp: new Date()
        }]);
        if (res.data.agentUsed) setLastAgent(res.data.agentUsed);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${res.data.message}`, timestamp: new Date() }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.response?.data?.message || err.message}`, timestamp: new Date() }]);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (date) => {
    const d = date ? new Date(date) : new Date();
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            Live Test
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Preview your AI Orchestrator in a realistic WhatsApp environment.
          </Typography.Paragraph>
        </div>
        <Space>
          {lastAgent && (
            <Tag color="blue" icon={<RobotOutlined />}>Active: {lastAgent}</Tag>
          )}
          {messages.length > 0 && (
            <Popconfirm title="Clear history?" onConfirm={handleClear}>
              <Button icon={<DeleteOutlined />} danger size="small" type="text">Clear</Button>
            </Popconfirm>
          )}
        </Space>
      </div>

      <div style={{ 
        height: '75vh', 
        display: 'flex', 
        flexDirection: 'column', 
        borderRadius: 12, 
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
        border: '1px solid #d1d1d1'
      }}>
        {/* WhatsApp Header */}
        <div style={{ 
          background: WA_TEAL, 
          padding: '10px 16px', 
          display: 'flex', 
          alignItems: 'center', 
          color: '#fff',
          zIndex: 10
        }}>
          <ArrowLeftOutlined style={{ marginRight: 12, fontSize: 18 }} />
          <Avatar icon={<RobotOutlined />} style={{ backgroundColor: '#fff', color: WA_TEAL, marginRight: 12 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', fontSize: 16 }}>{account?.name || 'AI Orchestrator'}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>online</div>
          </div>
          <Space size="large" style={{ fontSize: 18 }}>
            <VideoCameraOutlined />
            <PhoneOutlined />
            <MoreOutlined />
          </Space>
        </div>

        {/* Chat Body */}
        <div style={{ 
          flex: 1, 
          background: WA_BG, 
          backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
          backgroundRepeat: 'repeat',
          padding: '16px 24px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 4
        }}>
          {loadingHistory && (
            <div style={{ textAlign: 'center', padding: 20 }}><Tag>Loading messages...</Tag></div>
          )}
          
          {!loadingHistory && messages.length === 0 && (
            <div style={{ alignSelf: 'center', marginTop: 40, background: '#fff9c4', padding: '6px 12px', borderRadius: 8, fontSize: 12, boxShadow: '0 1px 1px rgba(0,0,0,0.1)' }}>
              Messages are end-to-end encrypted. Send a message to start the test.
            </div>
          )}

          {messages.map((msg, i) => {
            const isUser = msg.role === 'user';
            const showTail = i === 0 || messages[i-1].role !== msg.role;
            
            return (
              <div key={i} style={{
                alignSelf: isUser ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                position: 'relative',
                marginBottom: 2
              }}>
                <div style={{
                  background: isUser ? WA_LIGHT_GREEN : '#fff',
                  padding: '6px 10px 8px 10px',
                  borderRadius: 8,
                  borderTopRightRadius: isUser && showTail ? 0 : 8,
                  borderTopLeftRadius: !isUser && showTail ? 0 : 8,
                  boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                  position: 'relative',
                  minWidth: 80
                }}>
                  {/* Bubble Tail */}
                  {showTail && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      width: 0,
                      height: 0,
                      borderTop: `10px solid ${isUser ? WA_LIGHT_GREEN : '#fff'}`,
                      [isUser ? 'right' : 'left']: -8,
                      [isUser ? 'borderLeft' : 'borderRight']: '10px solid transparent'
                    }} />
                  )}

                  <div style={{ fontSize: 14.5, color: '#303030', whiteSpace: 'pre-wrap', paddingBottom: 4 }}>
                    {msg.content}
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'flex-end', 
                    gap: 4, 
                    fontSize: 11, 
                    color: 'rgba(0,0,0,0.45)',
                    marginTop: -4
                  }}>
                    {msg.agentUsed && !isUser && (
                      <span style={{ fontStyle: 'italic', marginRight: 'auto', fontSize: 10 }}>via {msg.agentUsed}</span>
                    )}
                    {formatTime(msg.timestamp)}
                    {isUser && <CheckOutlined style={{ color: '#34b7f1', fontSize: 13 }} />}
                  </div>
                </div>
              </div>
            );
          })}
          
          {sending && (
            <div style={{ alignSelf: 'flex-start', background: '#fff', padding: '8px 12px', borderRadius: 8, boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography.Text type="secondary" italic fontSize={12}>AI is typing...</Typography.Text>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* WhatsApp Input Bar */}
        <div style={{ background: WA_INPUT_BG, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ 
            flex: 1, 
            background: '#fff', 
            borderRadius: 24, 
            padding: '4px 16px', 
            display: 'flex', 
            alignItems: 'center',
            boxShadow: '0 1px 1px rgba(0,0,0,0.1)'
          }}>
            <TextArea
              value={input}
              onChange={e => setInput(e.target.value)}
              onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Type a message"
              autoSize={{ minRows: 1, maxRows: 6 }}
              bordered={false}
              style={{ flex: 1, padding: '8px 0', fontSize: 16 }}
              disabled={sending}
            />
          </div>
          <Button 
            type="primary" 
            shape="circle" 
            icon={<SendOutlined />} 
            onClick={handleSend}
            disabled={!input.trim() || sending}
            style={{ 
              backgroundColor: WA_GREEN, 
              width: 48, 
              height: 48, 
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default LiveTest;
